import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Search } from "@fg/icons";
import { useSearch } from "@/api/hooks";
import type { DriveItem } from "@/api/driveApi";
import { formatBytes } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ItemIcon } from "@/features/drive/items/ItemIcon";

/**
 * Debounced Drive search with a suggestions dropdown (↑/↓/Enter/Esc).
 * A folder hit opens the folder; a file hit opens its folder and selects +
 * previews it (?select=<id>&open=1). Enter with nothing highlighted shows
 * the full results page. Below md it expands over the top bar.
 */
export function SearchBox({ mobileOpen, onMobileClose }: { mobileOpen: boolean; onMobileClose: () => void }) {
    const navigate = useNavigate();
    const [value, setValue] = useState("");
    const [debounced, setDebounced] = useState("");
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(value), 200);
        return () => window.clearTimeout(t);
    }, [value]);

    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        window.addEventListener("mousedown", onDown);
        return () => window.removeEventListener("mousedown", onDown);
    }, []);

    useEffect(() => {
        if (mobileOpen) inputRef.current?.focus();
    }, [mobileOpen]);

    const search = useSearch(debounced);
    const hits = search.data ?? [];

    useEffect(() => {
        setActiveIndex(-1);
    }, [debounced]);

    const reset = () => {
        setOpen(false);
        setValue("");
        setActiveIndex(-1);
        onMobileClose();
    };

    const onPick = (item: DriveItem) => {
        if (item.kind === "Folder") {
            navigate(`/folder/${encodeURIComponent(item.id)}`);
        } else if (item.ownerEmail) {
            // Someone else's file: its folder may not be open to the user —
            // /open works out where it can be shown.
            navigate(`/open/${encodeURIComponent(item.id)}`);
        } else {
            const folder = item.parentId ? `/folder/${encodeURIComponent(item.parentId)}` : "/";
            navigate(`${folder}?select=${encodeURIComponent(item.id)}&open=1`);
        }
        reset();
    };

    const showAllResults = () => {
        const q = value.trim();
        if (!q) return;
        navigate(`/search?q=${encodeURIComponent(q)}`);
        setOpen(false);
        setActiveIndex(-1);
        onMobileClose();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            e.preventDefault();
            reset();
            (e.target as HTMLInputElement).blur();
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActiveIndex((i) => Math.min(hits.length - 1, i + 1));
            return;
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(-1, i - 1));
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            const hit = activeIndex >= 0 ? hits[activeIndex] : undefined;
            if (hit) onPick(hit);
            else showAllResults();
        }
    };

    return (
        <div
            ref={wrapRef}
            className={cn(
                // Below md the open search covers the whole top bar, in the bar's own colours.
                mobileOpen
                    ? "absolute inset-0 z-50 flex items-center gap-1 bg-[var(--appbar-bg)] px-2 text-[var(--appbar-fg)] md:static md:w-full md:max-w-[560px] md:bg-transparent md:px-0"
                    : "relative hidden w-full max-w-[560px] md:block"
            )}
        >
            {mobileOpen ? (
                <button
                    type="button"
                    onClick={onMobileClose}
                    aria-label="Close search"
                    className="suite-appbar-btn flex h-9 w-9 shrink-0 items-center justify-center rounded md:hidden"
                >
                    <ArrowLeft className="h-5 w-5" />
                </button>
            ) : null}
            <div className="relative min-w-0 flex-1">
                <Search className="suite-appbar-input-muted pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2" aria-hidden />
                <input
                    ref={inputRef}
                    type="search"
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    role="combobox"
                    aria-expanded={open && Boolean(debounced)}
                    aria-controls="drive-search-listbox"
                    aria-autocomplete="list"
                    aria-activedescendant={activeIndex >= 0 ? `drive-search-opt-${activeIndex}` : undefined}
                    placeholder="Search in Drive"
                    aria-label="Search Drive"
                    className="suite-appbar-input h-8 w-full rounded border-0 pl-9 pr-9 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--appbar-fg)_55%,transparent)]"
                />
                {search.isFetching && debounced ? (
                    <Loader2 className="suite-appbar-input-muted absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin" aria-hidden />
                ) : null}
                {open && debounced ? (
                    <div
                        id="drive-search-listbox"
                        role="listbox"
                        aria-label="Search suggestions"
                        className="absolute left-0 right-0 top-full z-[100] mt-1.5 max-h-96 overflow-auto rounded-xl border bg-popover py-1 text-popover-foreground shadow-xl"
                    >
                        {hits.length === 0 && !search.isFetching ? (
                            <div className="px-4 py-3 text-sm text-muted-foreground">No matches for &ldquo;{debounced}&rdquo;</div>
                        ) : (
                            hits.map((h, idx) => (
                                <button
                                    key={h.id}
                                    id={`drive-search-opt-${idx}`}
                                    type="button"
                                    role="option"
                                    aria-selected={idx === activeIndex}
                                    onMouseEnter={() => setActiveIndex(idx)}
                                    onClick={() => onPick(h)}
                                    className={cn(
                                        "flex w-full items-center gap-3 px-4 py-2 text-left text-sm",
                                        idx === activeIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground"
                                    )}
                                >
                                    <ItemIcon item={h} />
                                    <span className="flex-1 truncate">{h.name}</span>
                                    {h.ownerEmail ? (
                                        <span className="max-w-[10rem] shrink-0 truncate text-xs text-muted-foreground" title={`Shared by ${h.ownerEmail}`}>
                                            {h.ownerEmail}
                                        </span>
                                    ) : null}
                                    {h.kind === "File" && h.sizeBytes > 0 ? (
                                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatBytes(h.sizeBytes)}</span>
                                    ) : null}
                                </button>
                            ))
                        )}
                        <button
                            type="button"
                            onClick={showAllResults}
                            className="flex w-full items-center gap-3 border-t px-4 py-2.5 text-left text-sm text-muted-foreground hover:bg-accent"
                        >
                            <Search className="h-4 w-4" />
                            See all results for &ldquo;{debounced}&rdquo;
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
